import { Component, EventEmitter, Input, Output, signal } from '@angular/core'
import { AppStateService } from 'src/app/core/services/app-state.service'
import { AuthService } from 'src/app/core/services/auth.service'
import { SupplementalSign } from 'src/app/shared/models/matched-sign.model'
import { CustomerReportingService } from '../../services/customer-reporting.service'
import { getDefaultSignReportTitle, SignReport } from '../../models/sign-report.model'

export const SIGN_ISSUE_CATEGORIES = [
  { value: 'wrong-speed', label: 'Wrong speed' },
  { value: 'wrong-supplementary', label: 'Wrong supplementary' },
  { value: 'wrong-sign', label: 'Wrong sign' },
  { value: 'sign-missing', label: 'Sign missing' },
  { value: 'other', label: 'Other' },
] as const

@Component({
  selector: 'app-sign-report-dialog',
  templateUrl: './sign-report-dialog.component.html',
  styleUrl: '../report-form.scss',
  standalone: false,
})
export class SignReportDialogComponent {
  @Input() signId = ''
  @Input() longitude: number | null = null
  @Input() latitude: number | null = null
  @Input() signType = ''
  @Input() speedLimit: number | null = null
  @Input() displayType = ''
  @Input() supplementarySigns: SupplementalSign[] = []
  @Output() closed = new EventEmitter<void>()

  public report: SignReport = this.emptyReport()
  public readonly reportSubmitted = signal(false)
  public readonly reportSubmitting = signal(false)
  public readonly reportError = signal(false)
  public issueCategories = SIGN_ISSUE_CATEGORIES
  public readonly ticketKey = signal<string | null>(null)
  public readonly ticketUrl = signal<string | null>(null)

  constructor(
    private readonly customerReportingService: CustomerReportingService,
    private readonly appState: AppStateService,
    private readonly authService: AuthService,
  ) { }

  ngOnInit() {
    this.report = this.createInitialReport()
  }

  close() {
    this.closed.emit()
  }

  async submitReport() {
    this.report.mapLink = typeof window !== 'undefined' ? window.location.href : null
    this.reportSubmitting.set(true)
    this.reportError.set(false)
    try {
      const response = await this.customerReportingService.submitReport(this.report)
      this.ticketKey.set(response.ticketKey)
      this.ticketUrl.set(response.ticketUrl)
      this.reportSubmitting.set(false)
      this.reportSubmitted.set(true)
    } catch {
      this.reportSubmitting.set(false)
      this.reportError.set(true)
    }
  }

  private createInitialReport(): SignReport {
    const account = this.authService.getAccountInfo()
    const datasource =
      this.appState.selectDatasource() ?? this.authService.getUserClaims().userDatasources[0] ?? ''
    const now = new Date()
    const todayLocal = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16)

    return {
      reportType: 'existing-sign',
      title: getDefaultSignReportTitle({ reportType: 'existing-sign', signType: this.signType }),
      reporter: account?.name ?? account?.username ?? '',
      datasource,
      mapLink: typeof window !== 'undefined' ? window.location.href : null,
      signId: this.signId || null,
      signType: this.signType,
      speedLimit: this.speedLimit,
      displayType: this.displayType,
      supplementarySigns: this.supplementarySigns.map((sign) => sign.type ?? ''),
      occurrenceDateTime: todayLocal,
      category: '',
      description: '',
      location:
        this.latitude !== null && this.longitude !== null
          ? { lat: this.latitude, lng: this.longitude }
          : null,
      bearing: null,
    }
  }

  private emptyReport(): SignReport {
    return {
      reportType: 'existing-sign',
      title: '',
      reporter: '',
      datasource: '',
      mapLink: null,
      signType: '',
      speedLimit: null,
      displayType: '',
      supplementarySigns: [],
      category: '',
      description: '',
      signId: null,
      occurrenceDateTime: null,
      location: null,
      bearing: null,
    }
  }
}
